import React, { useState } from 'react';
import { AutoComplete, Form, Select, Tooltip, message } from 'antd';
import { UserAddOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { workwiseServices } from '../../../Api/api';
import { MindMapContext } from '../../../Context/contex';
import PeopleWithAccess from './peopleWithAccess';
import { useMemberSuggestions } from './useMemberSuggestions';
import './shareForm.css';

/** The id the dialog footer's Share button submits through `form=`. */
export const INVITE_FORM_ID = 'share-invite-form';

const RIGHT_TYPES = { reader: 1, collaborator: 2 };
const SUCCESS_STATUS = 200;

const validate = ({ selectedMember, inputValue, rightType }) => {
    const errors = {};
    if (!selectedMember.key || selectedMember.value !== inputValue) errors.person = 'Pick a person from the suggestions.';
    if (rightType === null) errors.rights = 'Choose a right.';
    return errors;
};

/** The invite view: a person and a right, then the people who already have access. */
const ShareForm = ({ onClose, onBack, setSubmitting }) => {
    const { documentId } = useParams();
    const { documentInfo, setDocumentInfo } = MindMapContext();
    const { options, search, clear } = useMemberSuggestions(documentInfo?.members);
    const [rightType, setRightType] = useState(null);
    const [selectedMember, setSelectedMember] = useState({});
    const [inputValue, setInputValue] = useState('');
    const [errors, setErrors] = useState({});

    const handleSearch = (value) => {
        setInputValue(value);
        setErrors((prev) => ({ ...prev, person: null }));
        if (value) {
            search(value);
            return;
        }
        clear();
        setSelectedMember({});
    };

    const handleSelect = (value) => {
        const selectedUser = options.find((user) => user.value === value);
        if (selectedUser) setSelectedMember(selectedUser);
    };

    const handleRights = (value) => {
        setErrors((prev) => ({ ...prev, rights: null }));
        setRightType(RIGHT_TYPES[value] ?? null);
    };

    const handleAddMember = async () => {
        const nextErrors = validate({ selectedMember, inputValue, rightType });
        setErrors(nextErrors);
        if (nextErrors.person || nextErrors.rights) return;

        setSubmitting(true);
        const payload = [{ id: documentInfo.referenceId, memberId: selectedMember.key, memberRightType: rightType }];
        try {
            await workwiseServices.addDocumentMember(documentInfo.referenceId, payload);
        } catch (error) {
            message.error("Something went wrong....");
            setSubmitting(false);
            return;
        }
        onClose();
        message.success("Document shared successfully");

        const response = await workwiseServices.getDocumentById(documentId);
        if (response?.status === SUCCESS_STATUS && response.data?.data && response.data.data.rightType !== 0) {
            setDocumentInfo(response.data.data);
        }
    };

    return (
        <div className="restricted-container">
            <div className="share-section-head">
                <Tooltip title="Back to general access" placement="bottomLeft">
                    <button type="button" className="share-back" aria-label="Back to general access" onClick={onBack}>
                        <ArrowLeftOutlined />
                    </button>
                </Tooltip>
                <p className="share-section-label">Invite people</p>
            </div>
            <Form id={INVITE_FORM_ID} aria-label="Invite people" className="share-form" layout="vertical" onFinish={handleAddMember}>
                <div className="share-fields">
                    <div className="share-field share-field--person">
                        <label className="share-field__label" htmlFor="share-person">
                            Person
                            <span className="share-field__req" aria-hidden="true">*</span>
                        </label>
                        <div className={`share-control ${errors.person ? "share-control--invalid" : ""}`.trim()}>
                            <UserAddOutlined className="share-control__icon" />
                            <Form.Item className="share-control__field">
                                <AutoComplete
                                    id="share-person"
                                    className="share-input"
                                    options={options}
                                    onSelect={handleSelect}
                                    onSearch={handleSearch}
                                    onChange={setInputValue}
                                    placeholder="Add people by name"
                                    notFoundContent="No data"
                                />
                            </Form.Item>
                        </div>
                        {errors.person && <span className="share-field__error" role="alert">{errors.person}</span>}
                    </div>
                    <div className="share-field share-field--rights">
                        <label className="share-field__label" htmlFor="share-rights">
                            Rights
                            <span className="share-field__req" aria-hidden="true">*</span>
                        </label>
                        <Form.Item className={`share-control__select ${errors.rights ? "share-control__select--invalid" : ""}`.trim()}>
                            <Select
                                id="share-rights"
                                onChange={handleRights}
                                placeholder="Select"
                                options={[{ value: 'reader', label: 'Reader' }, { value: 'collaborator', label: 'Collaborator' }]}
                            />
                        </Form.Item>
                        {errors.rights && <span className="share-field__error" role="alert">{errors.rights}</span>}
                    </div>
                </div>
            </Form>
            <PeopleWithAccess />
        </div>
    );
};

export default ShareForm;
